import { type CellContext, type ColumnDef } from '@tanstack/react-table'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { MoreHorizontal, Edit, Trash2, ArrowUpDown } from 'lucide-react'
import { cn } from '@/lib/utils'
import { UserActionDialog } from '../UserActionDialog'
import { useState } from 'react'
import { showSuccessToast } from '../ui/toast'
import AddTagDrawer from './AddTagDrawer'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip'

export interface Tag {
  id: string
  name: string
  events: number
  createdAt: string
  status: 'active' | 'inactive'
  isTrending: boolean
}

interface TagColumnsOptions {
  onEdit?: (tag: Tag) => void
  onDelete?: (tag: Tag) => void
}

const getCell = (accessorKey: string, options?: TagColumnsOptions) => {
  return ({ row, getValue }: CellContext<Tag, unknown>) => {
    const tag = row.original
    const [dialogOpen, setDialogOpen] = useState(false)
    const [isEditTagOpen, setIsEditTagOpen] = useState(false)
    const hasEvents = tag.events > 0
    const handleTagSubmit = () => {
      showSuccessToast('Tag updated.', 'The tag has been successfully updated.')
    }

    const handleDeleteConfirm = async () => {
      setDialogOpen(false)
      if (!hasEvents) {
        options?.onDelete?.(tag)
      }
    }

    switch (accessorKey) {
      case 'name':
        return (
          <div className="flex items-center gap-3">
            <div className="text-sm font-semibold text-ss-neutral-950 leading-4.5 ">
              {getValue() as string}
            </div>
            {tag.isTrending && (
              <Badge
                variant="outline"
                className={cn(
                  'px-1.5 py-0 text-[10px] font-semibold rounded-full leading-[17px]',
                  'bg-ss-blue-50 text-ss-blue-500 border-ss-blue-100'
                )}
              >
                Trending
              </Badge>
            )}
          </div>
        )

      case 'users':
        return (
          <div className="text-sm font-medium text-ss-neutral-600 leading-4.5 ">
            {getValue() as number}
          </div>
        )

      case 'actions':
        return (
          <>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="ghost"
                  className="h-8 p-0 w-full justify-end"
                  aria-label="Open menu"
                >
                  <MoreHorizontal className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-24">
                <DropdownMenuItem onClick={() => setIsEditTagOpen(true)}>
                  <Edit className="mr-2 h-4 w-4" />
                  Edit
                </DropdownMenuItem>
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <div>
                        <DropdownMenuItem
                          onClick={() => !hasEvents && setDialogOpen(true)}
                          className={cn(
                            hasEvents
                              ? 'text-ss-neutral-400 cursor-not-allowed opacity-50'
                              : 'text-ss-critical-200'
                          )}
                          disabled={hasEvents}
                        >
                          <Trash2 className="mr-2 h-4 w-4" />
                          Delete
                        </DropdownMenuItem>
                      </div>
                    </TooltipTrigger>
                    {hasEvents && (
                      <TooltipContent side="left">
                        <p className="text-xs">
                          This tag has {tag.events} active{' '}
                          {tag.events === 1 ? 'event' : 'events'} and cannot be
                          deleted.
                        </p>
                      </TooltipContent>
                    )}
                  </Tooltip>
                </TooltipProvider>
              </DropdownMenuContent>
            </DropdownMenu>
            <UserActionDialog
              open={dialogOpen}
              onOpenChange={setDialogOpen}
              showForm={false}
              onConfirm={handleDeleteConfirm}
              title="Delete tag?"
              subtext={`Are you sure you want to delete "${tag.name}"? This action cannot be undone.`}
              confirmButtonText="Delete Tag"
            />

            <AddTagDrawer
              open={isEditTagOpen}
              onOpenChange={setIsEditTagOpen}
              onSubmit={handleTagSubmit}
              data={{
                id: tag.id,
                name: tag.name,
                events: tag.events,
              }}
            />
          </>
        )

      default:
        return getValue() as React.ReactNode
    }
  }
}

export const getTagColumns = (
  options?: TagColumnsOptions
): ColumnDef<Tag>[] => [
  {
    accessorKey: 'name',
    header: 'Tag',
    cell: getCell('name', options),
    enableSorting: true,
  },
  {
    accessorKey: 'events',
    header: ({ column }) => {
      return (
        <Button
          variant="ghost"
          onClick={() => column.toggleSorting(column.getIsSorted() === 'asc')}
        >
          Events Using
          <ArrowUpDown className="ml-2 h-4 w-4" />
        </Button>
      )
    },
    cell: getCell('users', options),
    enableSorting: true,
  },
  {
    id: 'actions',
    header: '',
    cell: getCell('actions', options),
    enableSorting: false,
  },
]

// Default columns without options for backward compatibility
export const tagColumns: ColumnDef<Tag>[] = getTagColumns()
